import { useStore, useReactFlow } from '@xyflow/react'
import React, {
  memo,
  useCallback,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react'
import { shallow } from 'zustand/shallow'
import { nodeColor } from '@/constants/nodeTypes'
import MiniMapNodes from './MiniMapNodes'
import './FlowFullMap.css'

interface FlowFullMapProps {
  setShowNodeIndex: (value: boolean) => void
  handleNodeSelect: (nodeId: string) => void
  projectName?: string
}

const FlowFullMap: React.FC<FlowFullMapProps> = memo(
  ({ setShowNodeIndex, handleNodeSelect, projectName }) => {
    const { setViewport, getZoom, getViewport } = useReactFlow()
    const svgRef = useRef<SVGSVGElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const lastPointRef = useRef<{ x: number; y: number } | null>(null)
    const [isPanning, setIsPanning] = useState(false)
    const [isExiting, setIsExiting] = useState(false)
    const [clickedNodeId, setClickedNodeId] = useState<string | null>(null)

    const nodes = useStore(
      (s) =>
        Array.from(s.nodeLookup.values()).map((node) => {
          const { x, y } = node.internals?.positionAbsolute || { x: 0, y: 0 }
          const width = node.width || 100
          const height = node.height || 50
          return { id: node.id, x, y, width, height }
        }),
      shallow
    )

    const { viewBox, boundingRect } = useMemo(() => {
      if (!nodes.length) {
        return { viewBox: '0 0 1000 1000', boundingRect: null }
      }

      const containerWidth = window.innerWidth
      const containerHeight = window.innerHeight

      const bounds = nodes.reduce(
        (acc, node) => ({
          minX: Math.min(acc.minX, node.x),
          minY: Math.min(acc.minY, node.y),
          maxX: Math.max(acc.maxX, node.x + node.width),
          maxY: Math.max(acc.maxY, node.y + node.height),
        }),
        { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
      )

      const contentWidth = bounds.maxX - bounds.minX
      const contentHeight = bounds.maxY - bounds.minY
      const padding = 0.1
      const scaledWidth = contentWidth / containerWidth
      const scaledHeight = contentHeight / containerHeight
      const viewScale = Math.max(scaledWidth, scaledHeight)
      const viewWidth = viewScale * containerWidth
      const viewHeight = viewScale * containerHeight
      const offset = 5 * viewScale
      const x = bounds.minX - (viewWidth - contentWidth) / 2 - offset
      const y = bounds.minY - (viewHeight - contentHeight) / 2 - offset
      const width = viewWidth + offset * 2
      const height = viewHeight + offset * 2

      return {
        viewBox: `${x} ${y} ${width} ${height}`,
        boundingRect: bounds,
      }
    }, [nodes])

    const exitView = useCallback(() => {
      const currentZoom = getZoom()
      const { x, y } = getViewport()
      const newZoom = currentZoom * 1.1
      setViewport({ x, y, zoom: newZoom })

      setIsExiting(true)
      setTimeout(() => {
        setShowNodeIndex(false)
      }, 400)
    }, [getZoom, getViewport, setViewport, setShowNodeIndex])

    const handleNodeSelectWithEffect = useCallback(
      (nodeId: string) => {
        setClickedNodeId(nodeId)
        setTimeout(() => {
          handleNodeSelect(nodeId)
          setClickedNodeId(null)
        }, 200)
      },
      [handleNodeSelect]
    )

    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (e.button === 1) {
          const currentZoom = getZoom()
          const { x, y } = getViewport()
          setViewport({ x, y, zoom: currentZoom + 0.1 })
          return
        }

        setIsPanning(true)
        lastPointRef.current = { x: e.clientX, y: e.clientY }
        e.preventDefault()
      },
      [getZoom, getViewport, setViewport]
    )

    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        if (!isPanning || !lastPointRef.current || !svgRef.current) return

        const { x: prevX, y: prevY } = lastPointRef.current
        const dx = e.clientX - prevX
        const dy = e.clientY - prevY

        const { x, y, zoom } = getViewport()
        const newX = x - dx * zoom
        const newY = y - dy * zoom

        setViewport({ x: newX, y: newY, zoom })
        lastPointRef.current = { x: e.clientX, y: e.clientY }
      },
      [isPanning, getViewport, setViewport]
    )

    const handleMouseUp = useCallback(() => {
      setIsPanning(false)
      lastPointRef.current = null
    }, [])

    const handleWheel = useCallback(
      (e: WheelEvent) => {
        e.preventDefault()
        const currentZoom = getZoom()
        const { x, y } = getViewport()
        const zoomDelta = e.deltaY * -0.001
        const newZoom = Math.min(Math.max(currentZoom + zoomDelta, 0.1), 5)
        setViewport({ x, y, zoom: newZoom })
      },
      [getZoom, getViewport, setViewport]
    )

    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      container.addEventListener('wheel', handleWheel, { passive: false })

      return () => {
        container.removeEventListener('wheel', handleWheel)
      }
    }, [handleWheel])

    const [isHovered, setIsHovered] = useState(false)

    return (
      <div
        ref={containerRef}
        className={`flow-fullmap-container ${isPanning ? 'panning' : ''} ${
          isExiting ? 'fade-out' : 'fade-in'
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {projectName && (
          <div className="project-title">
            <div className="ml-5 relative">
              <p className="glitch-text" data-text={projectName}>
                {projectName}
              </p>
            </div>
          </div>
        )}
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox={viewBox}
          className="minimap-svg"
        >
          <defs>
            <filter id="ambient-glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient
              id="grid-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="rgba(99, 102, 241, 0.1)" />
              <stop offset="100%" stopColor="rgba(236, 72, 153, 0.1)" />
            </linearGradient>
          </defs>
          <MiniMapNodes
            nodeStrokeWidth={2}
            nodeComponent={MiniMapNode}
            nodeColor={nodeColor}
            onClick={handleNodeSelectWithEffect}
            containerWidth={window.innerWidth}
            containerHeight={window.innerHeight}
            contentBounds={boundingRect}
            clickedNodeId={clickedNodeId}
          />
        </svg>

        <button className="exit-button" onClick={exitView}>
          Exit View
        </button>
      </div>
    )
  },
  (prevProps, nextProps) =>
    prevProps.setShowNodeIndex === nextProps.setShowNodeIndex &&
    prevProps.handleNodeSelect === nextProps.handleNodeSelect &&
    prevProps.projectName === nextProps.projectName
)

const MiniMapNode = memo(
  ({
    x,
    y,
    width,
    height,
    color,
    strokeWidth,
    id,
    onClick,
    data,
    type,
    containerWidth,
    containerHeight,
    contentBounds,
    isHovered,
    onHover,
    isClicked,
  }: any) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (id && onClick) {
        requestAnimationFrame(() => onClick(id))
      }
    }

    const handleMouseEnter = () => {
      if (onHover) onHover(id)
    }

    const handleMouseLeave = () => {
      if (onHover) onHover(null)
    }

    let scalingFactor = 1
    if (contentBounds && containerWidth && containerHeight) {
      const viewBoxWidth = contentBounds.maxX - contentBounds.minX
      const viewBoxHeight = contentBounds.maxY - contentBounds.minY
      const widthScale = containerWidth / viewBoxWidth
      const heightScale = containerHeight / viewBoxHeight
      scalingFactor = Math.min(widthScale, heightScale)
    }

    const desiredFontSize = 18
    const adjustedFontSize = desiredFontSize / (scalingFactor || 1)
    const desiredLineSpacing = 6
    const adjustedLineSpacing = desiredLineSpacing / (scalingFactor || 1)
    const desiredPadding = 12
    const adjustedPadding = desiredPadding / (scalingFactor || 1)

    const electricBorderOffset = 4 / (scalingFactor || 1)

    // Helper function to format date
    const formatDate = (dateString: string) => {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch {
        return dateString.substring(0, 16)
      }
    }

    // Simplified truncate function
    const truncateText = (text: string, maxLength: number) => {
      if (!text) return ''
      return text.length > maxLength
        ? `${text.substring(0, maxLength)}...`
        : text
    }

    return (
      <g
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`${isClicked ? 'node-clicked' : ''} ${isHovered ? 'node-hovered' : ''}`}
        style={{
          opacity: isHovered ? 1 : 0.75,
          transition: 'opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <defs>
          <linearGradient
            id={`node-gradient-${id}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop
              offset="0%"
              stopColor={color || '#6366f1'}
              stopOpacity="0.9"
            />
            <stop
              offset="100%"
              stopColor={color || '#6366f1'}
              stopOpacity="0.6"
            />
          </linearGradient>
          <filter id={`glow-${id}`}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <pattern
            id={`dots-${id}`}
            x="0"
            y="0"
            width="4"
            height="4"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="2" cy="2" r="0.5" fill="rgba(255,255,255,0.1)" />
          </pattern>
        </defs>

        {/* Main node background */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={`url(#node-gradient-${id})`}
          stroke={color || '#6366f1'}
          strokeWidth={strokeWidth || 1}
          rx={8}
          ry={8}
          style={{ cursor: 'pointer' }}
          filter={isHovered ? `url(#glow-${id})` : 'none'}
          className={isHovered ? 'electric-glow' : ''}
        />

        {/* Subtle pattern overlay */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={`url(#dots-${id})`}
          rx={8}
          ry={8}
          style={{ cursor: 'pointer' }}
        />

        {/* Border highlight */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={0.5}
          rx={8}
          ry={8}
        />

        {/* Electric flow border when hovered */}
        {isHovered && (
          <>
            <line
              x1={x - electricBorderOffset}
              y1={y - electricBorderOffset}
              x2={x + width + electricBorderOffset}
              y2={y - electricBorderOffset}
              className="electric-border"
              strokeLinecap="round"
            />
            <line
              x1={x + width + electricBorderOffset}
              y1={y - electricBorderOffset}
              x2={x + width + electricBorderOffset}
              y2={y + height + electricBorderOffset}
              className="electric-border"
              strokeLinecap="round"
            />
            <line
              x1={x + width + electricBorderOffset}
              y1={y + height + electricBorderOffset}
              x2={x - electricBorderOffset}
              y2={y + height + electricBorderOffset}
              className="electric-border"
              strokeLinecap="round"
            />
            <line
              x1={x - electricBorderOffset}
              y1={y + height + electricBorderOffset}
              x2={x - electricBorderOffset}
              y2={y - electricBorderOffset}
              className="electric-border"
              strokeLinecap="round"
            />
          </>
        )}

        {/* Content area - UPDATED to use minified-info */}
        <g
          transform={`translate(${x + adjustedPadding}, ${y + adjustedPadding})`}
        >
          {/* Header with type only */}
          <rect
            x={0}
            y={0}
            width={width - adjustedPadding * 2}
            height={adjustedFontSize * 0.9}
            fill="rgba(0,0,0,0.6)"
            rx={3}
          />

          <text
            fill="rgba(255,255,255,0.95)"
            fontSize={adjustedFontSize * 0.6}
            fontFamily="Inter, sans-serif"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="central"
            x={(width - adjustedPadding * 2) / 2}
            y={(adjustedFontSize * 0.9) / 2}
          >
            {type?.toUpperCase() || 'NODE'}
          </text>

          {/* Main label */}
          <text
            fill="white"
            fontSize={adjustedFontSize * 0.6}
            fontFamily="Inter, sans-serif"
            fontWeight="500"
            textAnchor="start"
            dominantBaseline="hanging"
            x={4}
            y={adjustedFontSize + 10}
          >
            {truncateText(String(data?.label || 'Untitled'), 25)}
          </text>

          {/* Minified info - following NodeBaseContainer pattern */}
          {data?.minifiedInfo &&
            (() => {
              const minifiedEntries = Object.entries(data.minifiedInfo).filter(
                ([key, value]) =>
                  value && typeof value === 'string' && value.trim().length > 0
              )

              return minifiedEntries.length > 0 ? (
                <>
                  {minifiedEntries.slice(0, 3).map(([key, value], index) => (
                    <g key={key}>
                      {/* Key/label */}
                      <text
                        fill="rgba(255,255,255,0.7)"
                        fontSize={adjustedFontSize * 0.45}
                        fontFamily="Inter, sans-serif"
                        fontWeight="600"
                        textAnchor="start"
                        dominantBaseline="hanging"
                        x={4}
                        y={
                          adjustedFontSize * 2 +
                          25 +
                          index * adjustedFontSize * 1.2
                        }
                      >
                        {key}
                      </text>
                      {/* Value */}
                      <text
                        fill="rgba(255,255,255,0.6)"
                        fontSize={adjustedFontSize * 0.4}
                        fontFamily="Inter, sans-serif"
                        textAnchor="start"
                        dominantBaseline="hanging"
                        x={4}
                        y={
                          adjustedFontSize * 2 +
                          90 +
                          index * adjustedFontSize * 1.2
                        }
                      >
                        {truncateText(String(value), 35)}
                      </text>
                    </g>
                  ))}
                </>
              ) : null
            })()}

          {/* Fallback for nodes without minified info */}
          {(!data?.minifiedInfo ||
            Object.keys(data.minifiedInfo).length === 0) &&
            data?.description && (
              <text
                fill="rgba(255,255,255,0.6)"
                fontSize={adjustedFontSize * 0.5}
                fontFamily="Inter, sans-serif"
                textAnchor="start"
                dominantBaseline="hanging"
                x={4}
                y={adjustedFontSize * 3 + 12}
              >
                {truncateText(data.description, 35)}
              </text>
            )}

          {/* Creator at bottom - very simple */}
          <text
            fill="rgba(255,255,255,0.4)"
            fontSize={adjustedFontSize * 0.4}
            fontFamily="Inter, sans-serif"
            textAnchor="start"
            dominantBaseline="alphabetic"
            x={4}
            y={height - adjustedPadding * 2 - 2}
          >
            {data?.createdBy
              ? truncateText(data.createdBy.split('@')[0] || data.createdBy, 12)
              : ''}
          </text>
        </g>
      </g>
    )
  }
)

export default FlowFullMap
